// services/moderation.ts
// Moderation service for reviewing and managing flagged content.
// Write operations (approve/reject) are routed through the adminModeratePost
// callable Cloud Function which writes to adminActionLogs/ for every action.

import {
    collection,
    query,
    where,
    getDocs,
    orderBy,
    limit as firestoreLimit,
} from 'firebase/firestore';
import type {
    AdminModeratePostRequest,
    AdminModeratePostResponse,
} from '@omore/shared-types';
import { db } from '../firebase/config';
import { callWithIdempotency } from '../firebase/callable';
import type { ModerationQueueItem, ReportItem } from '../types';

/**
 * Fetch moderation queue with reports (read-only).
 */
export async function fetchModerationQueue(
    limitCount: number = 50
): Promise<ModerationQueueItem[]> {
    try {
        const queueQuery = query(
            collection(db, 'barkBoard', 'moderation_queue', 'items'),
            orderBy('flaggedAt', 'desc'),
            firestoreLimit(limitCount)
        );
        const snapshot = await getDocs(queueQuery);

        const queueItems: ModerationQueueItem[] = [];

        for (const docSnap of snapshot.docs) {
            const postData = docSnap.data();

            const reportsQuery = query(
                collection(db, 'barkBoard', 'reports', 'items'),
                where('postId', '==', docSnap.id),
                orderBy('timestamp', 'desc')
            );
            const reportsSnapshot = await getDocs(reportsQuery);

            const reports: ReportItem[] = reportsSnapshot.docs.map(reportDoc => {
                const data = reportDoc.data();
                return {
                    id: reportDoc.id,
                    postId: data.postId,
                    reporterId: data.reporterId,
                    reporterUsername: data.reporterUsername,
                    category: data.category,
                    details: data.details || '',
                    timestamp: data.timestamp?.toDate() || new Date(),
                    status: data.status,
                    resolution: data.resolution,
                    resolvedAt: data.resolvedAt?.toDate(),
                };
            });

            queueItems.push({
                post: {
                    id: docSnap.id,
                    type: postData.type,
                    title: postData.title,
                    caption: postData.caption,
                    category: postData.category,
                    authorName: postData.authorName,
                    authorEmail: postData.authorEmail,
                    authorId: postData.authorId || '',
                    createdAt: postData.createdAt?.toDate() || new Date(),
                    publishedAt: postData.publishedAt?.toDate(),
                    status: postData.status,
                    viewCount: postData.viewCount || 0,
                    complimentCount: postData.complimentCount || 0,
                    videoUrl: postData.videoUrl,
                    audioUrl: postData.audioUrl,
                    thumbnailUrl: postData.thumbnailUrl,
                    articleContent: postData.articleContent,
                    wordCount: postData.wordCount,
                    readingTime: postData.readingTime,
                },
                flaggedReason: postData.flaggedReason || 'Unknown',
                flaggedAt: postData.flaggedAt?.toDate() || new Date(),
                reportCount: reports.length,
                reports,
            });
        }

        return queueItems;
    } catch (error) {
        console.error('Error fetching moderation queue:', error);
        throw error;
    }
}

/**
 * Approve a flagged post via the adminModeratePost Cloud Function.
 * The CF updates the post, removes it from the queue, resolves reports,
 * and writes an entry to adminActionLogs/.
 */
export async function approvePost(
    postId: string,
    _adminId: string,   // identity now derived from auth token inside the CF
    _adminEmail: string,
    adminNote?: string
): Promise<void> {
    await callWithIdempotency<AdminModeratePostRequest, AdminModeratePostResponse>(
        'adminModeratePost',
        { postId, action: 'approve', adminNote }
    );
}

/**
 * Reject a flagged post via the adminModeratePost Cloud Function.
 * The CF optionally hard-deletes the post, removes it from the queue,
 * resolves reports, and writes an entry to adminActionLogs/.
 */
export async function rejectPost(
    postId: string,
    _adminId: string,
    _adminEmail: string,
    adminNote?: string,
    permanentDelete: boolean = false
): Promise<void> {
    await callWithIdempotency<AdminModeratePostRequest, AdminModeratePostResponse>(
        'adminModeratePost',
        { postId, action: 'reject', adminNote, permanentDelete }
    );
}

/**
 * Get moderation stats (read-only).
 */
export async function getModerationStats() {
    try {
        const queueSnapshot = await getDocs(
            collection(db, 'barkBoard', 'moderation_queue', 'items')
        );

        const pendingReportsQuery = query(
            collection(db, 'barkBoard', 'reports', 'items'),
            where('status', '==', 'pending')
        );
        const pendingSnapshot = await getDocs(pendingReportsQuery);

        const resolvedReportsQuery = query(
            collection(db, 'barkBoard', 'reports', 'items'),
            where('status', '==', 'resolved')
        );
        const resolvedSnapshot = await getDocs(resolvedReportsQuery);

        // Count moderation actions from adminActionLogs/ (replaces barkBoard/admin_logs/).
        const logsQuery = query(
            collection(db, 'adminActionLogs'),
            where('action', 'in', ['approve_post', 'reject_post', 'delete_post'])
        );
        const logsSnapshot = await getDocs(logsQuery);

        let approvedCount = 0;
        let rejectedCount = 0;

        logsSnapshot.docs.forEach(docSnap => {
            const data = docSnap.data();
            if (data.action === 'approve_post') approvedCount++;
            if (data.action === 'reject_post' || data.action === 'delete_post') rejectedCount++;
        });

        const totalModerated = approvedCount + rejectedCount;
        const approvalRate = totalModerated > 0 ? (approvedCount / totalModerated) * 100 : 0;

        return {
            queueSize: queueSnapshot.size,
            pendingReports: pendingSnapshot.size,
            resolvedReports: resolvedSnapshot.size,
            approvedPosts: approvedCount,
            rejectedPosts: rejectedCount,
            approvalRate: Math.round(approvalRate),
        };
    } catch (error) {
        console.error('Error fetching moderation stats:', error);
        return {
            queueSize: 0,
            pendingReports: 0,
            resolvedReports: 0,
            approvedPosts: 0,
            rejectedPosts: 0,
            approvalRate: 0,
        };
    }
}
